import React, { Suspense, useEffect, useCallback, useMemo } from 'react';
import { Button, InputGroup, FormControl } from 'react-bootstrap';
import pluralize from 'pluralize';
import _ from 'lodash';

import Spinner from '../../Spinner/Spinner';
import { getListState, saveListState } from '../../../lib/ListStateHelper';

const Table = React.lazy(() => import('../../Table/Table/Table'));
const ConfirmationModal = React.lazy(() => import('../../Modals/ConfirmationModal/ConfirmationModal'));
const FormInput = React.lazy(() => import('../../FormInput/FormInput'));

export default function BaseList(props) {
    const listKey = props.listKey || window.location.pathname;
    const savedState = useMemo(() => getListState(listKey) || {}, [listKey]);

    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState();
    const [activeFilter, setActiveFilter] = React.useState(savedState.activeFilter !== false);
    const [monthToMonthFilter, setMonthToMonthFilter] = React.useState(savedState.monthToMonthFilter !== false);
    const [satelliteOfficesFilter, setSatelliteOfficesFilter] = React.useState(savedState.satelliteOfficesFilter !== false);
    const [filteredList, setFilteredList] = React.useState([]);
    const [searchValue, setSearchValue] = React.useState(savedState.search || '');
    const [page, setPage] = React.useState(savedState.page || 1);
    const [sizePerPage, setSizePerPage] = React.useState(savedState.sizePerPage || props.pageSize || 50);

    const persistState = useCallback((updates) => {
        saveListState(listKey, updates);
    }, [listKey]);

    const filterList = useCallback((listData) => {
        let filtered = listData;

        if (!_.isEmpty(listData) && !_.isEmpty(props.columns)) {
            const filteredData = listData.filter((item) => {
                return props.activeValue === item[props.activeField]
            });

            filtered = filteredData;
        }

        return filtered;
    }, [props.activeField, props.activeValue, props.columns]);

    const searchList = (listData, searchValue) => {
        let filtered = listData;

        if (!_.isEmpty(listData)) {
            filtered = listData.filter((data) => {
                let valid = false;
                let item = null;

                for(let c = 0; c < props.columns.length; c++) {
                    const dataField = props.columns[c].dataField;
                    item = data[dataField];

                    if (dataField.includes('.')) {
                        var fields = dataField.split('.');
                        item = data[fields[0]]

                        if (item && fields.length > 1) {
                            for(let f = 1; f < fields.length; f++) {
                                item = item[fields[f]];
                            }
                        }
                    }

                    if (typeof item === 'string') {
                        valid = item.toLowerCase().includes(searchValue.toLowerCase());
                        if (valid) { break };
                    }
                }

                return (valid) ? item : null;
            });
        }

        return filtered;
    };

    const applyFilters = useCallback((listData) => {
        let filtered = listData || [];

        // Search is independent from the active-status filter.
        if (!_.isEmpty(searchValue)) {
            filtered = searchList(
                filtered,
                searchValue.toLowerCase()
            );
        }

        if (!props.hideFilter) {
            if (activeFilter === true) {
                filtered = filterList(filtered);
            }

            if (props.compType === 'BranchOffice') {
                if (monthToMonthFilter === false) {
                    filtered = filtered.filter(
                        item => item.monthToMonth === false
                    );
                }

                if (satelliteOfficesFilter === false) {
                    filtered = filtered.filter(
                        item => item.satelliteOffice === false
                    );
                }
            }
        }

        return filtered;
    }, [
        props.hideFilter,
        props.compType,
        searchValue,
        activeFilter,
        monthToMonthFilter,
        satelliteOfficesFilter,
        filterList,
        props.columns
    ]);

    useEffect(() => {
        setFilteredList(applyFilters(props.listData));
    }, [props.listData, applyFilters]);

    const refreshHandler = () => {
        if (props.refreshed) { props.refreshed(); }
    };

    const addHandler = () => {
        if (props.added) { props.added(); }
    };

    const copyHandler = (selectedItem) => {
        if (props.copied) { props.copied(selectedItem); }
    };

    const selectHandler = (selectedItem) => {
        if (props.selected) { props.selected(selectedItem); }
    };

    const customHandler = (selectedItem) => {
        if (props.custom) { props.custom(selectedItem); }
    };

    const changedHandler = (id, value, index, row) => {
        if (props.changed) { props.changed(id, value, index, row); }
    };

    const printHandler = (selectedItem) => {
        if (props.printed) { props.printed(selectedItem); }
    };

    const deleteHandler = (selectedItem) => {
        setShowDeleteModal(true);
        setSelectedItem(selectedItem);
    };

    const noDeleteHandler = () => {
        setShowDeleteModal(false);
    };

    const yesDeleteHandler = () => {
        setShowDeleteModal(false);

        if (props.deleted) { props.deleted(selectedItem); }
    };

    const searchHandler = (event) => {
        const value = event.target.value;
        setSearchValue(value);
        setPage(1);
        persistState({ search: value, page: 1 });
    };

    const resetHandler = () => {
        setSearchValue('');
        setPage(1);
        persistState({ search: '', page: 1 });
    };

    const activeFilterHandler = (_id, value) => {
        setActiveFilter(value);
        setPage(1);
        persistState({ activeFilter: value, page: 1 });

        if (props.filtered) { props.filtered(value); }
    };

    const monthToMonthFilterHandler = (_id, value) => {
        setMonthToMonthFilter(value);
        setPage(1);
        persistState({ monthToMonthFilter: value, page: 1 });

        if (props.filtered) { props.filtered(value); }
    };

    const satelliteOfficesFilterHandler = (_id, value) => {
        setSatelliteOfficesFilter(value);
        setPage(1);
        persistState({ satelliteOfficesFilter: value, page: 1 });

        if (props.filtered) { props.filtered(value); }
    };

    const pageChangedHandler = (nextPage, nextPageSize) => {
        // avoid repeating updates if values are unchanged (prevents nested update loops)
        if (nextPage !== page) { setPage(nextPage); }
        if (nextPageSize !== sizePerPage) { setSizePerPage(nextPageSize); }
        if (nextPage !== page || nextPageSize !== sizePerPage) {
            persistState({ page: nextPage, sizePerPage: nextPageSize });
        }

        if (_.isFunction(props.pageChanged)) {
            props.pageChanged(nextPage, nextPageSize);
        }
    };

    const pageSizeChangedHandler = (nextPage, nextPageSize) => {
        // avoid repeating updates if values are unchanged (prevents nested update loops)
        if (nextPage !== page) { setPage(nextPage); }
        if (nextPageSize !== sizePerPage) { setSizePerPage(nextPageSize); }
        if (nextPage !== page || nextPageSize !== sizePerPage) {
            persistState({ page: nextPage, sizePerPage: nextPageSize });
        }

        if (_.isFunction(props.pageSizeChanged)) {
            props.pageSizeChanged(nextPage, nextPageSize);
        }
    };

    const plural = pluralize(props.compType, 0);
    const noDataText = `No ${plural} Available.`;
    const keyField = _.isEmpty(props.keyField) ? 'id' : props.keyField;

    let table = (
        <Table keyField={keyField}
               responsive
               data={filteredList}
               sorting={props.sorting}
               columns={props.columns}
               isDisabled={props.isDisabled}
               isBusy={props.isBusy}
               hideEdit={props.hideEdit}
               hideDelete={props.hideDelete}
               hideCopy={props.hideCopy}
               hideAdd={props.hideAdd}
               hideRefresh={props.hideRefresh}
               hideSizePerPage={props.hideSizePerPage}
               hidePagination={props.hidePagination}
               hidePrint={props.hidePrint}
               showCustom={props.showCustom}
               customIcon={props.customIcon}
               customText={props.customText}
               editCondition={props.editCondition}
               hasCheckbox={props.hasCheckbox}
               permissionEdit={props.permissionEdit}
               showActions={props.showActions}
               noDataText={noDataText}
               xscroll={props.xscroll}
               page={page}
               pageSize={sizePerPage}
               pageChanged={(nextPage, nextPageSize) => pageChangedHandler(nextPage, nextPageSize)}
               pageSizeChanged={(nextPage, nextPageSize) => pageSizeChangedHandler(nextPage, nextPageSize)}
               printed={(selectedItem) => printHandler(selectedItem)}
               selected={(selectedItem) => selectHandler(selectedItem)}
               custom={(selectedItem) => customHandler(selectedItem)}
               deleted={(selectedItem) => deleteHandler(selectedItem)}
               copied={(selectedItem) => copyHandler(selectedItem)}
               changed={(id, value, index, row) => changedHandler(id, value, index, row)} />
    );

    let addButton = null;
    let tableActions = null;
    let deleteModal = null;

    if (props.isBusy) {
        table = <Spinner text={`Loading ${plural}...`} />;
    } else {
        const refreshButton = (!props.hideRefresh) && (
            <Button bsStyle="default" onClick={refreshHandler} className="button-group-margin">
                <i className="fa fa-refresh button-icon"></i>
                Refresh
            </Button>
        );

        if (props.permissionEdit && !props.hideAdd) {
            const buttonText = (props.addButtonText) ? props.addButtonText : `Add ${props.compType}`;

            addButton = (
                <Button bsStyle="danger" onClick={addHandler} className="button-group-margin" disabled={props.isAddedDisabled}>
                    <i className="fa fa-plus button-icon"></i>
                    {buttonText}
                </Button>
            );
        }

        const search = (!props.hideSearch) && (
            <InputGroup style={{ width: '400px' }}>
                <InputGroup.Addon>Search:</InputGroup.Addon>
                <FormControl id="search" type="text" maxLength={50}
                             value={searchValue}
                             autoComplete="off"
                             onChange={(event) => searchHandler(event)} />
                <InputGroup.Button>
                    <Button bsStyle="default" onClick={resetHandler}>Reset</Button>
                </InputGroup.Button>
            </InputGroup>
        );

        const filter = (!props.hideFilter) && (
            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px', gap: '12px' }}>
                <FormInput type="checkbox"
                           id="activeFilter"
                           value={activeFilter}
                           labelText="Show Active Only"
                           changed={(id, value) => activeFilterHandler(id, value)} />

                {props.compType === "BranchOffice" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <FormInput
                            type="checkbox"
                            id="satelliteOfficesFilter"
                            value={satelliteOfficesFilter}
                            labelText="Show Satellite Offices"
                            changed={(id, value) => satelliteOfficesFilterHandler(id, value)}
                        />

                        <FormInput
                            type="checkbox"
                            id="monthToMonthFilter"
                            value={monthToMonthFilter}
                            labelText="Show Month to Month"
                            changed={(id, value) => monthToMonthFilterHandler(id, value)}
                        />
                    </div>
                )}
            </div>
        );

        if (addButton || refreshButton || props.buttons || props.forceShowFilters) {
            tableActions = (
                <div style={{ display: 'flex', alignItems: 'center', paddingBottom: props.forceShowFilters ? 0 : '20px' }}>
                    <div style={{ width: '100%', minWidth: '30%', display: 'flex', alignItems: 'center' }}>
                        {search}
                        {props.moreFilters}
                        {filter}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '260px', minWidth: '260px' }}>
                        {refreshButton}
                        {props.buttons}
                        {addButton}
                    </div>
                </div>
            );
        }
    }

    if (showDeleteModal) {
        deleteModal = (
            <ConfirmationModal
                show={showDeleteModal}
                noClicked={noDeleteHandler}
                yesClicked={yesDeleteHandler}>
                <p><strong>If this {props.compType} is deleted, it and all related data will be unrecoverable.</strong></p>
                Are you sure you want to delete this {props.compType}?
            </ConfirmationModal>
        );
    }

    return (
        <Suspense fallback={<Spinner />}>
            {deleteModal}
            {tableActions}
            {table}
        </Suspense>
    );
}
